import React, { useContext, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getHeader, setHeader } from "@tanstack/react-start/server";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { Check } from "lucide-react";
import { languages } from "~/lib/languages";
import {
  createEmptyUser,
  createUserPreferences,
  getUserById,
  updateUserPreferences,
} from "~/lib/functions/user";
import { getCookieValue } from "~/lib/utils";
import NotFoundGraphic from "~/img/not-found-telescope.svg";
import ServerErrorGraphic from "~/img/server-error.svg";
import {
  UserPreferencesContext,
  UserPreferencesFormContext,
} from "~/contexts/user-preferences";
import { toast } from "sonner";
import type { CoachMatchPreferences } from "~/lib/matching";
import { globalFormData } from "~/lib/hooks/use-discovery-form";
import { createNoIndexMeta } from "~/lib/shared/meta";

const easeOutExpo = (t: number): number => {
  return 1 === t ? 1 : 1 - Math.pow(2, -10 * t);
};

const SaveUserPreferencesSchema = z.object({
  goals: z.array(z.string()),
  otherGoal: z.string().optional().nullable(),
  startTime: z.string(),
  support: z.string(),
  coachingFormats: z.string(),
  coachingApproaches: z.array(z.string()),
  coachingPackages: z.array(z.string()),
  coachGender: z.string(),
  coachAges: z.array(z.string()),
  userPersonality: z.array(z.string()),
  coachPersonality: z.array(z.string()),
  faithIntegration: z.array(z.string()),
  preferredLanguages: z.array(z.string()),
  personalContext: z.string(),
  sessionDeliveryMethods: z.string(),
  prefersCoachLocation: z.string(),
  coachLocationRegion: z.string().optional().nullable(),
  coachLocationCountry: z.string().optional().nullable(),
  otherFaithIntegration: z.string().optional().nullable(),
});

export const saveUserPreferences = createServerFn({ method: "POST" })
  .validator((data: unknown) => SaveUserPreferencesSchema.parse(data))
  .handler(async ({ data: values, context: _context }) => {
    const sanitizeText = (text: string | null | undefined): string => {
      if (!text) {
        return "Not specified";
      }
      // Clean text for embedding to avoid encoding issues
      return text
        .replace(/[\r\n\t]/g, " ") // Replace newlines/tabs with spaces
        .replace(/\s+/g, " ") // Normalize whitespace
        .replace(/[^\x20-\x7E\xA0-\uFFFF]/g, "") // Keep printable ASCII and Unicode
        .normalize("NFKC")
        .trim();
    };

    const embeddingPrompt = `
    This user is seeking a coach with the following preferences:
    
    Primary Coaching Needs:
    - Main goals: ${values.goals.map(sanitizeText).join(", ")}
    - Other goals: ${sanitizeText(values.otherGoal)}

    Coaching Format & Delivery:
    - Coaching Format: ${sanitizeText(values.coachingFormats)}
    - Session Format: ${sanitizeText(values.sessionDeliveryMethods)}

    Experience Level & Coaching Packages:
    - Coach Experience Levels: ${values.coachingPackages.map(sanitizeText).join(", ")}

    Coaching Style & Approach
    - Coaching Approaches: ${values.coachingApproaches.map(sanitizeText).join(", ")}
    
    Coach Characteristics Preferences:
    - Preferred coach gender: ${sanitizeText(values.coachGender) || "No preference"}
    - Faith/religion alignment: ${values.faithIntegration.map(sanitizeText).join(", ")}
    - Language requirements: ${values.preferredLanguages?.map(sanitizeText).join(", ") || "Not specified"}
    - Coach Ages: ${values.coachAges.map(sanitizeText).join(", ")}
    
    Location & Logistics:
    - Prefers coach location: ${sanitizeText(values.prefersCoachLocation)}
    - Coach location region: ${sanitizeText(values.coachLocationRegion)}
    - Coach location country: ${sanitizeText(values.coachLocationCountry)}
    
    Additional Context:
    - Other faith preference: ${sanitizeText(values.otherFaithIntegration)}
    `.trim();

    try {
      const cookieHeader = getHeader("cookie") || "";
      const userId = getCookieValue(cookieHeader, "userId") || "";

      // Dynamic imports for server-only modules
      const [
        { getAIPreferencesGenerator },
        { getCoachMatches },
        { serverEnv },
      ] = await Promise.all([
        import("~/lib/ai-preferences-generator"),
        import("~/lib/matching"),
        import("~/lib/server-env"),
      ]);

      const AiPreferencesGenerator = getAIPreferencesGenerator(
        serverEnv.EMBEDDINGS_OPENAI_API_KEY!,
        serverEnv.EMBEDDINGS_OPENAI_BASE_URL!,
      );

      const embedding =
        await AiPreferencesGenerator.createEmbedding(embeddingPrompt);

      const getCoaches = async () => {
        const coaches = await getCoachMatches({
          data: {
            preferences: { ...values, embedding } as CoachMatchPreferences,
            limit: 10, // Get more coaches to ensure we have enough
            queryLimit: 100,
          },
        });

        if (!coaches || 0 === coaches.length) {
          return null;
        }

        // For unauthenticated users, they can see coaches at indices 2 and 3 (0-based)
        // So we should navigate to the first accessible coach, which is index 2
        const accessibleCoachIndex = 2;

        if (coaches.length > accessibleCoachIndex) {
          const accessibleCoach = coaches[accessibleCoachIndex];
          if (accessibleCoach.coach.username) {
            return accessibleCoach.coach.username;
          }
        }

        // Fallback: find the first coach with a username among accessible coaches
        const accessibleCoaches = coaches.slice(2, 4); // Indices 2 and 3
        const coachWithUsername = accessibleCoaches.find(
          (item) => item.coach.username && "" !== item.coach.username,
        );

        if (coachWithUsername) {
          return coachWithUsername.coach.username;
        }

        // Last fallback: any coach with username (though this shouldn't be accessible)
        const anyCoachWithUsername = coaches.find(
          (item) => item.coach.username && "" !== item.coach.username,
        );

        return (
          anyCoachWithUsername?.coach.username ||
          coaches[0].coach.username ||
          null
        );
      };

      const existingUser = await getUserById({ data: { userId } });

      let coachName: string | null = null;

      if (!userId || !existingUser) {
        const user = await createEmptyUser();
        await createUserPreferences({
          data: {
            userId: user.id,
            preferences: {
              ...values,
              preferencesEmbedding: embedding,
            },
          },
        });

        // Set userId cookie for the newly created user
        setHeader(
          "Set-Cookie",
          `userId=${user.id}; Path=/; HttpOnly; SameSite=Lax`,
        );

        coachName = await getCoaches();
      } else {
        await updateUserPreferences({
          data: {
            userId,
            preferences: {
              ...values,
              preferencesEmbedding: embedding,
            },
          },
        });
        coachName = await getCoaches();
      }

      if (coachName) {
        return {
          status: "success",
          message: "Preferences processed successfully",
          data: { coachName },
        };
      }
      return {
        status: "not_found",
        message: "No suitable coaches found",
        data: { coachName: null },
      };
    } catch {
      // Return generic error to avoid exposing internal details
      return {
        status: "error",
        message: "Failed to save preferences",
        data: { coachName: null },
      };
    }
  });

interface StepPreference {
  progress: number;
  preference: {
    id: string;
    label: string;
    value: string | string[];
  };
}

type ProcessingState =
  | { type: "validating" }
  | { type: "processing"; progress: number }
  | { type: "success"; coachName: string }
  | { type: "not_found"; message: string }
  | { type: "error"; message: string };

function ProcessingPage() {
  const navigate = useNavigate({ from: "/discovery/steps/processing" });
  const [state, setState] = useState<ProcessingState>({ type: "validating" });
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const serverCallStarted = useRef(false);
  const animationDuration = 6000;

  const preferences = useContext(UserPreferencesContext);
  const formState = useContext(UserPreferencesFormContext);
  const savePreferences = useServerFn(saveUserPreferences);

  const processingStepsText = [
    { text: "Analyzing your preferences...", threshold: 10 },
    { text: "Finding the best coaches...", threshold: 40 },
    { text: "Tailoring your matches...", threshold: 75 },
    { text: "Finalizing results...", threshold: 95 },
  ];
  const currentProcessingStepIndex = React.useMemo(() => {
    return processingStepsText.findIndex(
      (step, index) =>
        displayPercentage < step.threshold ||
        index === processingStepsText.length - 1,
    );
  }, [displayPercentage]);

  const preferenceSteps = React.useMemo(() => {
    if (!preferences) {
      return [];
    }

    const rawPrefs = preferences;
    const steps: StepPreference[] = [];

    // Add coaching format
    if (rawPrefs.coachingFormats) {
      steps.push({
        progress: 10,
        preference: {
          id: "type",
          label: "Type",
          value: rawPrefs.coachingFormats,
        },
      });
    }

    // Add session delivery method
    if (rawPrefs.sessionDeliveryMethods) {
      steps.push({
        progress: 20,
        preference: {
          id: "format",
          label: "Format",
          value: rawPrefs.sessionDeliveryMethods,
        },
      });
    }

    // Add goals
    if (rawPrefs.goals?.length) {
      rawPrefs.goals.forEach((goal, index) => {
        steps.push({
          progress: 30 + index * 5,
          preference: {
            id: `goal-${index}`,
            label: `Goal ${index + 1}`,
            value: goal,
          },
        });
      });
    }

    // Add coach gender
    if (rawPrefs.coachGender && "No preference" !== rawPrefs.coachGender) {
      steps.push({
        progress: 50,
        preference: {
          id: "gender",
          label: "Gender",
          value: rawPrefs.coachGender,
        },
      });
    }

    // Add preferred languages
    if (rawPrefs.preferredLanguages?.length) {
      rawPrefs.preferredLanguages.forEach((langCode, index) => {
        const lang = languages.find((l) => l.name === langCode);
        if (lang) {
          steps.push({
            progress: 60 + index * 5,
            preference: {
              id: `lang-${index}`,
              label: "Language",
              value: lang.native,
            },
          });
        }
      });
    }

    // Add coaching approaches
    if (rawPrefs.coachingApproaches?.length) {
      steps.push({
        progress: 75,
        preference: {
          id: "approaches",
          label: "Approaches",
          value: rawPrefs.coachingApproaches.join(", "),
        },
      });
    }

    // Add coaching packages
    if (rawPrefs.coachingPackages?.length) {
      steps.push({
        progress: 85,
        preference: {
          id: "packages",
          label: "Experience",
          value: rawPrefs.coachingPackages.join(", "),
        },
      });
    }

    // Add faith integration
    if (rawPrefs.faithIntegration?.length) {
      steps.push({
        progress: 90,
        preference: {
          id: "faith",
          label: "Faith Integration",
          value: rawPrefs.faithIntegration.join(", "),
        },
      });
    }

    steps.sort((a, b) => (a.progress ?? 0) - (b.progress ?? 0));

    return steps;
  }, [preferences]);

  const visiblePreferenceSteps = React.useMemo(() => {
    const visible = preferenceSteps.filter(
      (step) => displayPercentage >= (step.progress ?? 0),
    );
    return visible;
  }, [preferenceSteps, displayPercentage]);

  const navigateToCarousel = (coachName: string) => {
    if (!coachName) {
      setState({ type: "not_found", message: "No coach found" });
      return;
    }

    navigate({ to: "/discovery/$username", params: { username: coachName } });
  };

  const navigateToDiscovery = () => {
    navigate({ to: "/discovery/steps/1" });
  };

  // Validation effect - runs once on mount
  useEffect(() => {
    const hasCompletedSteps = formState?.steps.some((completed) => completed);
    const hasGlobalFormData = 0 < Object.keys(globalFormData).length;
    const hasPreferencesData =
      preferences &&
      Object.keys(preferences).some(
        (key) =>
          "id" !== key &&
          "createdAt" !== key &&
          "updatedAt" !== key &&
          "userId" !== key &&
          "preferencesEmbedding" !== key &&
          preferences[key as keyof typeof preferences],
      );

    if (!hasCompletedSteps && !hasGlobalFormData && !hasPreferencesData) {
      navigateToDiscovery();
      return;
    }

    if (!preferences) {
      setState({
        type: "error",
        message: "Missing preference data. Please go back.",
      });
      return;
    }

    // Start processing
    setState({ type: "processing", progress: 0 });
  }, [preferences, formState]);

  // Server processing effect - runs once when processing starts
  useEffect(() => {
    if (
      "processing" !== state.type ||
      !preferences ||
      serverCallStarted.current
    ) {
      return;
    }

    serverCallStarted.current = true;

    const processPreferences = async () => {
      try {
        // Save to localStorage for discovery flow
        if (preferences) {
          localStorage.setItem("userPreferences", JSON.stringify(preferences));
        }

        // Clear discovery form data since processing is complete
        localStorage.removeItem("discoveryFormData");

        const result = await savePreferences({ data: preferences });

        if ("success" === result.status && result.data.coachName) {
          setState({ type: "success", coachName: result.data.coachName });
        } else if ("not_found" === result.status) {
          const message = result.message || "No suitable coaches found.";
          setState({ type: "not_found", message });
          toast.info(message);
        } else {
          const message = result.message || "An unexpected error occurred.";
          setState({ type: "error", message });
          toast.error(message);
        }
      } catch {
        const message = "Failed to save preferences";
        setState({ type: "error", message });
        toast.error(message);
      }
    };

    processPreferences();
  }, [state.type, preferences]);

  // Animation effect - runs independently
  useEffect(() => {
    if ("processing" !== state.type) {
      return;
    }

    let startTime: number | null = null;
    let animationFrameId: number;

    const animate = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutExpo(progress);
      const newPercentage = Math.min(Math.floor(easedProgress * 100), 100);

      setDisplayPercentage(newPercentage);
      setState((prev) =>
        "processing" === prev.type
          ? { ...prev, progress: newPercentage }
          : prev,
      );

      if (1 > progress) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [state.type, animationDuration]);

  // Navigation effect - handles successful completion
  useEffect(() => {
    if ("success" === state.type) {
      // Add a small delay to show the success message briefly
      const timer = setTimeout(() => {
        navigateToCarousel(state.coachName);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [state.type]);

  const renderContent = () => {
    switch (state.type) {
      case "validating":
        return (
          <div className="text-lg text-gray-600">
            Validating your preferences...
          </div>
        );

      case "processing":
        return (
          <>
            <div className="relative h-32 w-32">
              <svg
                className="absolute left-0 top-0 h-full w-full -rotate-90 transform"
                viewBox="0 0 100 100"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="10"
                  className="text-gray-200"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="10"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (displayPercentage / 100) * 283}
                  className="text-brand-700 transition-[stroke-dashoffset] duration-300 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-3xl font-semibold">
                {displayPercentage}%
              </div>
            </div>
            <p className="text-lg text-gray-600">
              {processingStepsText[Math.max(0, currentProcessingStepIndex - 1)]
                ?.text || "Processing..."}
            </p>
            <div className="mt-4 w-full max-w-sm space-y-2">
              {visiblePreferenceSteps.map((step, index) => (
                <div
                  key={`${step.preference?.id}-${index}`}
                  className="animate-in fade-in flex items-center justify-start gap-2 rounded-md bg-gray-100 px-4 py-2 text-sm duration-500"
                >
                  <span className="font-medium text-gray-600">
                    {step.preference?.label || "Preference"}:
                  </span>
                  <span className="truncate text-gray-800">
                    {step.preference?.value || "Loading..."}
                  </span>
                  <Check className="ml-2 h-4 w-4 flex-shrink-0 text-green-500" />
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Hang tight, we're finding the best coaches for you!
            </p>
          </>
        );

      case "not_found":
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="h-40 w-40 text-gray-400">
              <NotFoundGraphic />
            </div>
            <h2 className="text-xl font-semibold">No Matches Found</h2>
            <p className="text-gray-600">
              We couldn't find coaches perfectly matching all your criteria at
              this moment.
            </p>
            <p className="text-sm text-gray-500">
              You might consider adjusting your preferences or checking back
              later.
            </p>
          </div>
        );

      case "error":
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="h-40 w-40 text-red-400">
              <ServerErrorGraphic />
            </div>
            <h2 className="text-xl font-semibold text-red-600">
              Processing Error
            </h2>
            <p className="text-gray-600">{state.message}</p>
            <p className="text-sm text-gray-500">
              Please try again later or contact support.
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-lg text-gray-600">
            Success! Redirecting to your coach...
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DiscoveryStep.Card>
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-8 text-center">
        {renderContent()}
      </div>
    </DiscoveryStep.Card>
  );
}

export const Route = createFileRoute("/_public/discovery/steps/processing/")({
  head: () =>
    createNoIndexMeta(
      "Processing Your Preferences – Dream Coach Match",
      "Finding the perfect coach for you based on your discovery responses",
    ),
  component: ProcessingPage,
});
